import axios from "axios";
import {
  API_ADMIN_REGISTRATION_REQUEST_APPROVE_ROUTE,
  API_ADMIN_REGISTRATION_REQUEST_REJECT_ROUTE,
  API_ADMIN_REGISTRATION_REQUEST_RESEND_EMAIL_ROUTE,
  API_ADMIN_REGISTRATION_REQUEST_ROUTE,
  API_ADMIN_REGISTRATION_REQUESTS_ROUTE,
  API_REGISTRATION_REQUESTS_ROUTE,
} from "../global/constants/routes/apiConstants";
import {
  AdminRegistrationRequestListParams,
  AdminRegistrationRequestListResponse,
  AdminRegistrationRequestResponse,
  FastApiErrorResponse,
  FastApiValidationDetail,
  RegistrationRequestInput,
  RegistrationRequestPayload,
  RegistrationRequestResponse,
  RegistrationRequestServiceError,
} from "../global/types/registrationRequest";
import { apiService } from "./api";

const CONFLICT_MESSAGE =
  "Já existe uma solicitação ou conta vinculada a este e-mail.";
const VALIDATION_MESSAGE =
  "Não foi possível validar os dados enviados. Revise os campos e tente novamente.";
const TEMPORARY_ERROR_MESSAGE =
  "O serviço de cadastro está temporariamente indisponível. Tente novamente.";
const UNKNOWN_ERROR_MESSAGE =
  "Não foi possível enviar a solicitação. Tente novamente.";

const toPayload = (
  input: RegistrationRequestInput
): RegistrationRequestPayload => ({
  full_name: input.fullName,
  email: input.email,
  institution_name: input.institutionName,
  institution_cnpj: input.institutionCnpj,
  institution_city: input.institutionCity,
});

const getValidationDetails = (
  data: FastApiErrorResponse | undefined
): FastApiValidationDetail[] => {
  const detail = data?.detail;
  return Array.isArray(detail) ? detail : [];
};

const normalizeError = (error: unknown): RegistrationRequestServiceError => {
  if (!axios.isAxiosError<FastApiErrorResponse>(error)) {
    return new RegistrationRequestServiceError(
      "UNKNOWN",
      UNKNOWN_ERROR_MESSAGE
    );
  }

  if (!error.response) {
    return new RegistrationRequestServiceError(
      "NETWORK",
      TEMPORARY_ERROR_MESSAGE
    );
  }

  const { status, data } = error.response;
  if (status === 409) {
    return new RegistrationRequestServiceError(
      "CONFLICT",
      CONFLICT_MESSAGE,
      status
    );
  }

  if (status === 422) {
    return new RegistrationRequestServiceError(
      "VALIDATION",
      VALIDATION_MESSAGE,
      status,
      getValidationDetails(data)
    );
  }

  if (status === 503) {
    return new RegistrationRequestServiceError(
      "UNAVAILABLE",
      TEMPORARY_ERROR_MESSAGE,
      status
    );
  }

  return new RegistrationRequestServiceError(
    "UNKNOWN",
    UNKNOWN_ERROR_MESSAGE,
    status
  );
};

export const createRegistrationRequest = async (
  input: RegistrationRequestInput
): Promise<RegistrationRequestResponse> => {
  try {
    const response = await apiService.post<RegistrationRequestResponse>(
      API_REGISTRATION_REQUESTS_ROUTE,
      toPayload(input)
    );
    return response.data;
  } catch (error) {
    throw normalizeError(error);
  }
};

export const listAdminRegistrationRequests = async ({
  status,
  offset = 0,
  limit = 25,
}: AdminRegistrationRequestListParams = {}): Promise<AdminRegistrationRequestListResponse> => {
  const response = await apiService.get<AdminRegistrationRequestListResponse>(
    API_ADMIN_REGISTRATION_REQUESTS_ROUTE,
    {
      params: {
        status,
        offset,
        limit,
      },
    }
  );
  return response.data;
};

export const getAdminRegistrationRequest = async (
  id: number
): Promise<AdminRegistrationRequestResponse> => {
  const response = await apiService.get<AdminRegistrationRequestResponse>(
    API_ADMIN_REGISTRATION_REQUEST_ROUTE(id)
  );
  return response.data;
};

export const approveAdminRegistrationRequest = async (
  id: number
): Promise<AdminRegistrationRequestResponse> => {
  const response = await apiService.post<AdminRegistrationRequestResponse>(
    API_ADMIN_REGISTRATION_REQUEST_APPROVE_ROUTE(id)
  );
  return response.data;
};

export const rejectAdminRegistrationRequest = async (
  id: number,
  rejectionReason: string
): Promise<AdminRegistrationRequestResponse> => {
  const response = await apiService.post<AdminRegistrationRequestResponse>(
    API_ADMIN_REGISTRATION_REQUEST_REJECT_ROUTE(id),
    { rejection_reason: rejectionReason }
  );
  return response.data;
};

export const resendAdminRegistrationRequestEmail = async (
  id: number
): Promise<AdminRegistrationRequestResponse> => {
  const response = await apiService.post<AdminRegistrationRequestResponse>(
    API_ADMIN_REGISTRATION_REQUEST_RESEND_EMAIL_ROUTE(id)
  );
  return response.data;
};
